import React, { useState, useMemo } from 'react';
import polyline from '@mapbox/polyline';
import { FaWalking, FaBus, FaMapMarkerAlt, FaRuler, FaClock } from 'react-icons/fa';
import { busApi } from '../../services/api/busApi';
import { useVehicleTracking } from '../../hooks/useVehicleTracking';

const LegIcon = ({ mode }) => {
  if (mode === 'WALK') return <FaWalking />;
  if (mode === 'BUS') return <FaBus />;
  return null;
};

function RoutePlanner({ onRouteFound, onViewDetails, onVehiclesUpdate, onVehicleClick }) {
  const [origem, setOrigem] = useState('');
  const [destino, setDestino] = useState('');
  const [loading, setLoading] = useState(false);
  const [routePlan, setRoutePlan] = useState(null);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [currentScreen, setCurrentScreen] = useState('form'); // 'form' | 'routes' | 'details'
  const [vehiclesExpanded, setVehiclesExpanded] = useState(false);

  // Extrair números das linhas de ônibus da rota selecionada (routeShortName)
  const busRouteShortNames = useMemo(() => {
    const itinerary = routePlan?.itineraries?.[selectedRouteIndex];
    if (!itinerary) return [];

    return itinerary.legs
      .filter(leg => leg.mode === 'BUS' && leg.routeShortName)
      .map(leg => leg.routeShortName); // Ex: ["2102", "2109"]
  }, [routePlan, selectedRouteIndex]);

  // Rastrear todos os veículos (backend não aceita filtro por routeShortName ainda)
  const { vehicles: allVehicles, loading: vehiclesLoading } = useVehicleTracking(
    {}, // Buscar todos os veículos
    15000,
    busRouteShortNames.length > 0 && currentScreen === 'details'
  );

  // Filtrar veículos pelas linhas da rota
  const vehicles = useMemo(() => {
    if (!busRouteShortNames.length || !allVehicles.length) return [];
    
    return allVehicles.filter(vehicle => 
      busRouteShortNames.includes(vehicle.routeShortName)
    );
  }, [allVehicles, busRouteShortNames]);

  // Notificar componente pai sobre veículos (para passar ao mapa)
  React.useEffect(() => {
    if (onVehiclesUpdate) {
      onVehiclesUpdate(vehicles);
    }
  }, [vehicles, onVehiclesUpdate]);

  const geocodeAddress = async (address) => {
    if (!address) return null;
    try {
      const response = await busApi.geocode(address);
      const location = response.data.results?.[0];
      return location ? { lat: location.lat, lon: location.lng } : null;
    } catch (error) {
      return null;
    }
  };

  const handleSearch = async () => {
    setLoading(true);
    setRoutePlan(null);
    setSelectedRouteIndex(0);
    if (onRouteFound) onRouteFound(null);

    const coordsOrigem = await geocodeAddress(origem);
    const coordsDestino = await geocodeAddress(destino);

    if (coordsOrigem && coordsDestino) {
      try {
        const fromPlaceString = `${coordsOrigem.lat},${coordsOrigem.lon}`;
        const toPlaceString = `${coordsDestino.lat},${coordsDestino.lon}`;
        const now = new Date();
        const date = now.toISOString().split('T')[0];
        const time = now.toTimeString().substring(0, 5);

        const params = {
          fromPlace: fromPlaceString,
          toPlace: toPlaceString,
          date,
          time,
          mode: 'WALK,BUS',
          transitReluctanceForModes: 'BUS:0.5'
        };

        const response = await busApi.planRoute(params);

        setRoutePlan(response.data);

        if (response.data?.itineraries?.length > 0) {
          setCurrentScreen('routes');
        }
      } catch (error) {
        setRoutePlan(null);
      }
    }

    setLoading(false);
  };

  const handleRouteSelect = (index) => {
    setSelectedRouteIndex(index);
    const itinerary = routePlan?.itineraries?.[index];
    if (itinerary && onRouteFound) {
      const segments = itinerary.legs.map(leg => ({
        points: polyline.decode(leg.geometry),
        mode: leg.mode,
        routeShortName: leg.routeShortName || null,
        routeColor: leg.routeColor || null,
        routeLongName: leg.routeLongName || leg.tripHeadsign || leg.headsign,
        tripHeadsign: leg.tripHeadsign || leg.headsign,
        from: leg.from.name,
        to: leg.to.name,
        fromCoordinates: [leg.from.lat, leg.from.lng || leg.from.lon],
        toCoordinates: [leg.to.lat, leg.to.lng || leg.to.lon],
        intermediateStops: leg.intermediateStops || [],
        duration: leg.duration?.value || 0,
        // store numeric distance in meters
        distance: typeof leg.distance === 'object' && leg.distance !== null ? (leg.distance.inMeters || 0) : (leg.distance || 0),
        // keep human readable if available for UI that expects it
        distanceHuman: leg.distance?.humanReadable || null
      }));
      
      const routeDetails = {
        segments,
        summary: {
          origin: origem,
          destination: destino,
          duration: itinerary.duration?.value || 0,
          distance: itinerary.distance
        }
      };
      
      onRouteFound(segments.map(s => ({
        points: s.points,
        mode: s.mode,
        routeShortName: s.routeShortName,
        routeColor: s.routeColor
      })), routeDetails);
    }
    
    setCurrentScreen('details');
  };

  const handleBackToForm = () => {
    setCurrentScreen('form');
    setRoutePlan(null);
    setSelectedRouteIndex(0);
    if (onRouteFound) onRouteFound(null);
    if (onVehiclesUpdate) onVehiclesUpdate([]);
  };

  const handleBackToRoutes = () => {
    setCurrentScreen('routes');
    if (onRouteFound) onRouteFound(null);
    if (onVehiclesUpdate) onVehiclesUpdate([]);
  };

  const selectedItinerary = routePlan?.itineraries?.[selectedRouteIndex];
  const allItineraries = routePlan?.itineraries || [];

  // TELA 1: FORMULÁRIO
  if (currentScreen === 'form') {
    return (
      <div className="sheet-route-planner-container">
  <h2>🗺️ Planejar Rota</h2>
        
        <div className="input-group">
          <label>Origem</label>
          <input type="text" placeholder="Digite o endereço de partida" value={origem} onChange={(e) => setOrigem(e.target.value)} />
        </div>
        <div className="input-group">
          <label>Destino</label>
          <input type="text" placeholder="Digite o endereço de chegada" value={destino} onChange={(e) => setDestino(e.target.value)} />
        </div>

        <button type="button" onClick={handleSearch} disabled={loading || !origem || !destino} className="search-button">
          {loading ? 'Buscando...' : 'Buscar Rota'}
        </button>
        
  {!origem || !destino ? (
          <p style={{ textAlign: 'center', color: '#6b7280', fontSize: '14px', marginTop: '8px' }}>
            Preencha origem e destino para buscar
          </p>
        ) : null}

  {routePlan && allItineraries.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '20px', color: '#ef4444' }}>
            <p>❌ Nenhuma rota encontrada</p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>
              Tente ajustar os endereços ou a preferência de caminhada
            </p>
          </div>
        )}
      </div>
    );
  }

  // TELA 2: LISTA DE ROTAS
  if (currentScreen === 'routes') {
    return (
      <div className="sheet-route-planner-container">
        <button className="back-button" onClick={handleBackToForm}>
          ← Nova Busca
        </button>
        
        <h2>🗺️ Rotas Disponíveis</h2>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
          De <strong>{origem}</strong> para <strong>{destino}</strong>
        </p>
        
        <div className="route-options">
          <h4>
            {allItineraries.length === 1 
              ? 'Clique para ver detalhes' 
              : `${allItineraries.length} opções encontradas`}
          </h4>
          {allItineraries.map((itinerary, index) => (
            <div
              key={index}
              className="route-option-card"
              onClick={() => handleRouteSelect(index)}
            >
              <div className="route-option-header">
                <span className="route-time"><FaClock /> {itinerary.duration.humanReadable}</span>
                <span className="route-distance"><FaMapMarkerAlt /> {itinerary.distance.humanReadable || (typeof itinerary.distance === 'number' ? `${(itinerary.distance/1000).toFixed(2)} km` : itinerary.distance)}</span>
              </div>

              <div className="route-option-details">
                {itinerary.legs.map((leg, legIndex) => (
                  <div key={legIndex} className="route-leg-mini">
                    <LegIcon mode={leg.mode} />
                    <span className="leg-info">
                      {leg.mode === 'WALK' ? (
                        `${leg.duration.humanReadable} caminhando`
                      ) : (
                        <>
                          <strong className="bus-number">Linha {leg.routeShortName || leg.route?.shortName || '?'}</strong>
                          <span className="bus-name">{leg.routeLongName || leg.route?.longName || leg.tripHeadsign || leg.headsign || 'Informação não disponível'}</span>
                        </>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // TELA 3: DETALHES DA ROTA
  if (currentScreen === 'details' && selectedItinerary) {
    return (
      <div className="sheet-route-planner-container">
        <button className="back-button" onClick={handleBackToRoutes}>
          ← Ver Outras Rotas
        </button>
        
        <h2>📋 Detalhes da Rota</h2>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
          De <strong>{origem}</strong> para <strong>{destino}</strong>
        </p>

        {/* Informação de ônibus em tempo real */}
        {busRouteShortNames.length > 0 && (
          <div style={{ 
            padding: '12px', 
            background: '#f0f9ff', 
            borderRadius: '8px', 
            marginBottom: '16px',
            border: '1px solid #bae6fd'
          }}>
            <div 
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: vehicles.length > 0 ? 'pointer' : 'default'
              }}
              onClick={() => vehicles.length > 0 && setVehiclesExpanded(!vehiclesExpanded)}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>🚌</span>
                <div>
                  <strong style={{ color: '#0369a1' }}>
                    {vehicles.length} ônibus em tempo real
                  </strong>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>
                    {vehiclesLoading ? 'Atualizando...' : vehicles.length > 0 ? 'Clique para ver detalhes' : 'Visualize no mapa acima'}
                  </div>
                </div>
              </div>
              {vehicles.length > 0 && (
                <span style={{ fontSize: '18px', color: '#0369a1' }}>
                  {vehiclesExpanded ? '▲' : '▼'}
                </span>
              )}
            </div>

            {/* Lista de veículos expansível */}
            {vehiclesExpanded && vehicles.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid #bae6fd', paddingTop: '12px' }}>
                {vehicles.map((vehicle, index) => {
                  const vehicleDisplayId =
                    vehicle.vehiclePrefix ??
                    vehicle.vehicleId ??
                    vehicle.plate ??
                    null;

                  return (
                  <div 
                    key={vehicle.vehicleId || index}
                    style={{
                      padding: '8px',
                      background: 'white',
                      borderRadius: '6px',
                      marginBottom: index < vehicles.length - 1 ? '8px' : '0',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      border: '1px solid #e0f2fe'
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#0c4a6e', marginBottom: '4px' }}>
                        🚍 Linha {vehicle.routeShortName}
                        {vehicleDisplayId ? ` - Veículo #${vehicleDisplayId}` : ''}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {vehicle.speed > 0 
                          ? `🏃 ${Math.round(vehicle.speed * 3.6)} km/h` 
                          : '⏸️ Parado'}
                        {vehicle.stopId && ` • Na parada`}
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onVehicleClick) {
                          onVehicleClick(vehicle);
                        }
                      }}
                      style={{
                        padding: '6px 10px',
                        background: '#0369a1',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap',
                        width: 'auto',
                        flexShrink: 0
                      }}
                    >
                      📍 Ver no mapa
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        <div className="route-summary">
          <span>Duração: <strong>{selectedItinerary.duration.humanReadable}</strong></span>
          <span>Distância: <strong>{selectedItinerary.distance.humanReadable}</strong></span>
        </div>

        <ul className="legs-list">
            {selectedItinerary.legs.map((leg, index) => (
              <li key={index} className="leg-item">
                <div className="leg-icon"><LegIcon mode={leg.mode} /></div>
                <div className="leg-details">
                  <strong>
                    {leg.mode === 'WALK' 
                      ? `${index === 0 ? 'Caminhe' : 'Continue caminhando'} por ${leg.duration.humanReadable}` 
                      : `Pegue o ônibus Linha ${leg.routeShortName || '?'}`}
                  </strong>
                  {leg.mode === 'BUS' && (
                    <p style={{ color: '#10b981', fontWeight: 500 }}>
                      {leg.routeLongName || leg.tripHeadsign || leg.headsign || 'Sentido não especificado'}
                    </p>
                  )}
                  <p><strong>De:</strong> {leg.from.name}</p>
                  <p><strong>Para:</strong> {leg.to.name}</p>
                  {(leg.distance !== undefined && leg.distance !== null) && (
                    <p style={{ fontSize: '12px', color: '#9ca3af' }}>
                      <FaRuler /> {
                        typeof leg.distance === 'object' && leg.distance.humanReadable 
                          ? leg.distance.humanReadable 
                          : typeof leg.distance === 'number' 
                            ? `${(leg.distance / 1000).toFixed(2)} km` 
                            : leg.distance
                      }
                    </p>
                  )}
                </div>
              </li>
            ))}
        </ul>

        <div style={{ 
          padding: '12px', 
          background: '#f0fdf4', 
          borderRadius: '8px', 
          marginTop: '16px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0, color: '#10b981', fontWeight: 600 }}>
            ✅ Você chegará ao seu destino!
          </p>
        </div>

        {onViewDetails && (
          <button 
            onClick={onViewDetails}
            className="view-details-button"
            style={{
              width: '100%',
              padding: '14px',
              marginTop: '16px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'transform 0.2s ease'
            }}
          >
            📋 Ver Detalhes Expandidos
          </button>
        )}
      </div>
    );
  }

  // Fallback
  return null;
}

export default RoutePlanner;